import React from "react";
import styles from "./source-selection-section.module.scss";
import { MachineNameDropdown } from "@src/components/atoms/machine-name-dropdown/machine-name-dropdown";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import searchIcon from "@src/assets/svg/search.svg";
import saveIcon from "@src/assets/svg/save-disk-icon.svg";
import { useTicketSourceSelection } from "@src/cloud-connect/support/hooks/use-ticket-source-selection";
import { useI18N } from "@src/language/language-context";
import { SupportAgentTicket } from "@src/types/support";

const cssClassName = "source-selection-section";

const NOOP_REFRESH = () => {
  /* no refresh needed */
};

interface SourceSelectionSectionProps {
  ticketId: string;
  ticketSourceId: string | null;
  callbackForRefresh?: (ticket?: SupportAgentTicket) => void;
  isSaveDisabled?: boolean;
  sourceLabel: string;
  sourcePlaceholder: string;
  noSourcesFound: string;
  confirmButton: string;
}

export const SourceSelectionSection = ({
  ticketId,
  ticketSourceId,
  callbackForRefresh,
  isSaveDisabled = false,
  sourceLabel,
  sourcePlaceholder,
  noSourcesFound,
  confirmButton,
}: SourceSelectionSectionProps) => {
  const i18n = useI18N();
  const {
    sourceOptions,
    selectedSource,
    isLoading,
    isSelectionFixed,
    isPatchingSource,
    isSSEUnavailable,
    onSelectionChange,
    confirmSourceSelection,
  } = useTicketSourceSelection(
    ticketId,
    ticketSourceId,
    callbackForRefresh ?? NOOP_REFRESH,
  );

  const disabledTooltip = isSSEUnavailable
    ? i18n("modules.sse.reconnectingActionsUnavailableTooltip")
    : undefined;

  return (
    <div className={styles[cssClassName]} title={disabledTooltip}>
      <p className={styles[`${cssClassName}__label`]}>{sourceLabel}</p>
      <div className={styles[`${cssClassName}__controls`]}>
        <div className={styles[`${cssClassName}__machine-field`]}>
          <img
            className={styles[`${cssClassName}__machine-icon`]}
            src={searchIcon}
            alt=""
            aria-hidden="true"
          />
          <MachineNameDropdown
            options={sourceOptions}
            selectedMachine={selectedSource}
            onSelectMachine={onSelectionChange}
            machineLabelText={sourceLabel}
            hideLabel
            showDropdownToggle
            noMachineNamesText={noSourcesFound}
            placeholderText={sourcePlaceholder}
            isLoading={isLoading}
            disabled={isSelectionFixed || isPatchingSource || isSSEUnavailable}
          />
        </div>
        <ButtonWithLoader
          fun={(e) => {
            e.preventDefault();
            void confirmSourceSelection();
          }}
          tooltip={disabledTooltip || confirmButton}
          aria-label={confirmButton}
          variant="no-outline"
          loading={isPatchingSource}
          disabled={
            isSelectionFixed ||
            isSSEUnavailable ||
            !selectedSource ||
            isSaveDisabled
          }
          type="button"
          style={{ height: 40, padding: 0, width: 40 }}
        >
          <img
            className={styles[`${cssClassName}__save-icon`]}
            src={saveIcon}
            alt=""
            aria-hidden="true"
          />
        </ButtonWithLoader>
      </div>
    </div>
  );
};
